import { Injectable } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class MetaService {
  private defaultTitle = 'WorkLuge';
  private defaultDescription = 'WorkLuge - Project Management Platform';

  constructor(
    private titleService: Title,
    private metaService: Meta,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {
    this.initializeMetaTags();
  }

  private initializeMetaTags(): void {
    // Listen to route changes and update meta tags automatically
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map(route => {
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route;
      }),
      filter(route => route.outlet === 'primary'),
      mergeMap(route => route.data)
    ).subscribe(data => {
      this.updateMetaTags(data);
    });
  }

  updateMetaTags(data: any): void {
    // Update title
    if (data['title']) {
      this.titleService.setTitle(data['title']);
    } else {
      this.titleService.setTitle(this.defaultTitle);
    }

    // Update description
    if (data['description']) {
      this.metaService.updateTag({
        name: 'description',
        content: data['description']
      });
    } else {
      this.metaService.updateTag({
        name: 'description',
        content: this.defaultDescription
      });
    }

    // Update other meta tags if provided
    if (data['keywords']) {
      this.metaService.updateTag({
        name: 'keywords',
        content: data['keywords']
      });
    }

    if (data['author']) {
      this.metaService.updateTag({
        name: 'author',
        content: data['author']
      });
    }

    if (data['robots']) {
      this.metaService.updateTag({
        name: 'robots',
        content: data['robots']
      });
    }

    // Update Open Graph tags
    this.updateOpenGraphTags(data);
  }

  private updateOpenGraphTags(data: any): void {
    const title = data['title'] || this.defaultTitle;
    const description = data['description'] || this.defaultDescription;

    this.metaService.updateTag({
      property: 'og:title',
      content: title
    });

    this.metaService.updateTag({
      property: 'og:description',
      content: description
    });

    this.metaService.updateTag({
      property: 'og:type',
      content: 'website'
    });

    // Update Twitter Card tags
    this.metaService.updateTag({
      name: 'twitter:title',
      content: title
    });

    this.metaService.updateTag({
      name: 'twitter:description',
      content: description
    });

    this.metaService.updateTag({
      name: 'twitter:card',
      content: 'summary'
    });
  }

  // Method to manually update meta tags (for components that need custom logic)
  setTitle(title: string): void {
    this.titleService.setTitle(title);
  }

  setDescription(description: string): void {
    this.metaService.updateTag({
      name: 'description',
      content: description
    });
  }

  setKeywords(keywords: string): void {
    this.metaService.updateTag({
      name: 'keywords',
      content: keywords
    });
  }

  // Method to add custom meta tags
  addCustomMetaTag(tag: { name?: string; property?: string; content: string }): void {
    this.metaService.updateTag(tag);
  }

  // Method to remove meta tags
  removeMetaTag(selector: string): void {
    this.metaService.removeTag(selector);
  }
}
